import { useCallback, useEffect, useState } from 'react'

import { listSystemPrompts } from '../../api/api'
import { SystemPrompt } from '../../api/models'

type UseSystemPromptsResult = {
  prompts: SystemPrompt[]
  isLoading: boolean
  hasError: boolean
  refetch: () => void
  updateLocal: (prompt: SystemPrompt) => void
}

export const useSystemPrompts = (): UseSystemPromptsResult => {
  const [prompts, setPrompts] = useState<SystemPrompt[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [fetchCount, setFetchCount] = useState(0)

  const refetch = useCallback(() => setFetchCount(c => c + 1), [])

  useEffect(() => {
    let isSubscribed = true

    const load = async () => {
      setIsLoading(true)
      setHasError(false)
      try {
        const response = await listSystemPrompts()
        if (!isSubscribed) return

        setIsLoading(false)
        if (!response) {
          setPrompts([])
          setHasError(true)
          return
        }

        setPrompts(response)
      } catch {
        if (!isSubscribed) return
        setIsLoading(false)
        setPrompts([])
        setHasError(true)
      }
    }

    load()

    return () => {
      isSubscribed = false
    }
  }, [fetchCount])

  const updateLocal = useCallback((prompt: SystemPrompt) => {
    setPrompts(prev => prev.map(p => (p.key === prompt.key ? prompt : p)))
  }, [])

  return { prompts, isLoading, hasError, refetch, updateLocal }
}
